import React from "react";
import { KeyboardEvent } from "react";

import Box from "@mui/material/Box";

import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Tooltip from "@mui/material/Tooltip";

import { DateField } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";

import NumericFormatComponent from "./NumericFormatComponent";

import PercentIcon from "@mui/icons-material/Percent";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";

import FormInputRowModeTypes from "./FormInputRowModeTypes";
import FormInputRowConfig from "./FormInputRowConfig";
import FormInputRowMode from "./FormInputRowMode";

import FormRowState from "./FormRowState";
import FormInputRowStateToModeMap from "./FormInputRowModeTypesToGroupMap";
import FormRowModeStateType from "./FormRowModeStateType";

import FormControl from "@mui/material/FormControl";
import { SelectChangeEvent } from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";

import FormControlLabel from "@mui/material/FormControlLabel";
import FormHelperText from "@mui/material/FormHelperText";
import { SyntheticEvent } from "react";
import Checkbox from "@mui/material/Checkbox";
import Switch from "@mui/material/Switch";
import SymbolAutocomplete from "../SymbolUtilityComponents/SymbolAutocomplete";

import IconButton from "@mui/material/IconButton";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Collapse, Typography } from "@mui/material";

import FormConfig from "./FormConfig";
import ConfiguredForm from "./ConfiguredForm";

import Paper from "@mui/material/Paper";

import { LoadingButton } from "@mui/lab";
import { isMobile } from "react-device-detect";

const enterToolTipDelay = 400;

// TODO: make this a string union type
const numberModes = FormInputRowStateToModeMap.numberModes;
const dollarModes = ["dollar", "price"];
const stringModes = FormInputRowStateToModeMap.stringModes;
const symbolModes = FormInputRowStateToModeMap.symbolModes;
const selectModes = FormInputRowStateToModeMap.selectModes;
const fixedModes = FormInputRowStateToModeMap.fixedModes;
const singleDateModes = FormInputRowStateToModeMap.singleDateModes;
const booleanModes = FormInputRowStateToModeMap.booleanModes;
const formModes = FormInputRowStateToModeMap.formModes;
const refreshModes = FormInputRowStateToModeMap.refreshModes;

// TODO: make these better and add error checking

function onNumberModeValueChange(mode: FormInputRowModeTypes) {
  switch (mode) {
    case "float":
      return parseFloat;
    case "integer":
      return parseInt;
    case "percent":
      return parseFloat;
    case "dollar":
      return parseInt;
    case "price":
      return (s: any) => {
        const parsed = parseFloat(s);
        if (isNaN(parsed)) {
          return parsed;
        }
        if (parsed <= 0) {
          return parsed;
        }

        const rounded100 = Math.round(parsed * 100) / 100;

        if (parsed !== rounded100) {
          return parsed.toFixed(2);
        }

        return parsed;
      };
    default:
      return (s: any) => s;
  }
}

function getModeIconFromOption(option: FormInputRowMode) {
  return (
    option.modeIcon ||
    (dollarModes.includes(option.optionVariant) ? (
      <AttachMoneyIcon />
    ) : option.optionVariant === "percent" ? (
      <PercentIcon />
    ) : option.optionVariant === "refresh" ? (
      <AutoFixHighIcon />
    ) : null)
  );
}

function makeToggleButtonFromOptionIndex(option: FormInputRowMode, index: number) {
  const toggleButtonPayload = (
    <div>
      <>
        {getModeIconFromOption(option)}
        {option.modeText || null}
      </>
    </div>
  );

  return option.modeTooltipText !== undefined ? (
    <ToggleButton key={index} value={index}>
      <Tooltip
        title={option.modeTooltipText}
        placement="top-start"
        arrow
        key={option.modeText}
        enterDelay={enterToolTipDelay}
      >
        {toggleButtonPayload}
      </Tooltip>
    </ToggleButton>
  ) : (
    <ToggleButton key={index} value={index}>
      {toggleButtonPayload}
    </ToggleButton>
  );
}

function makeMenuItemFromOptionIndex(option: FormInputRowMode, index: number) {
  const menuItemPayload = (
    <div>
      <>
        {getModeIconFromOption(option)}
        {option.modeText || null}
      </>
    </div>
  );

  return option.modeTooltipText !== undefined ? (
    <MenuItem key={index} value={index}>
      <Tooltip
        title={option.modeTooltipText}
        placement="top-start"
        arrow
        key={option.modeText}
        enterDelay={enterToolTipDelay}
      >
        {menuItemPayload}
      </Tooltip>
    </MenuItem>
  ) : (
    <MenuItem key={index} value={index}>
      {menuItemPayload}
    </MenuItem>
  );
}

type FormInputRowProps = {
  config: FormInputRowConfig;
  setNestedConfigAtIndex: (
    newConfig: (FormConfig | undefined) | ((oldConfig: FormConfig | undefined) => FormConfig | undefined),
    nestedIndex: number
  ) => void;
  refresh: (endpoint: string | undefined) => void;
  refreshing: boolean;
  disabled?: boolean;
  catchEnterKeyDownToSubmit: (e: KeyboardEvent) => void;
  currentState: FormRowState;
  updateFormRowStateIndex: (newRowModeIndex: number) => void;
  updateFormRowStateAtIndex: (
    newState: FormRowModeStateType | ((oldState: FormRowState[]) => FormRowState[]),
    rowModeIndex?: number
  ) => void;
  updateFormRowStateAndErrorAtIndex: (
    newState: FormRowModeStateType | ((oldState: FormRowState[]) => FormRowState[]),
    newError: string | null,
    rowModeIndex?: number
  ) => void;
  updateFormRowErrorAtIndex: (newError: string | null, rowModeIndex?: number) => void;
};

function FormInputRow(props: FormInputRowProps) {
  // Sugar for interacting with the props without having to type props every time
  const config = props.config;
  const disabled = props.disabled || config.disabled;
  // TODO: assert input options length is nonzero
  const currentModeIndex = Math.max(0, Math.min(config.inputOptions.length, props.currentState.currentModeIndex));

  const [nestedRefreshing, setNestedRefreshing] = React.useState(false);

  const updateCurrentModeIndex = (newModeIndex: number) => props.updateFormRowStateIndex(newModeIndex);
  const updateCurrentModeState = (newState: FormRowModeStateType) => {
    if (!config.inputOptions[currentModeIndex].dontClearErrorOnInteraction) {
      props.updateFormRowStateAndErrorAtIndex(newState, null);
    } else {
      props.updateFormRowStateAtIndex(newState);
    }
  };
  const updateOtherModeState = (newState: FormRowModeStateType, rowModeIndex: number) =>
    props.updateFormRowStateAtIndex(newState, rowModeIndex);

  const currentModeState = props.currentState.currentModeStates[currentModeIndex];
  const currentInputOption = config.inputOptions[currentModeIndex];
  const currentFieldLabel = currentInputOption.fieldLabel || config.defaultFieldLabel;
  const currentFieldHint = currentInputOption.fieldHint || config.defaultFieldHint;

  const currentVariant: FormInputRowModeTypes = currentInputOption.optionVariant;

  const currentInputFieldToDisplay:
    | "text"
    | "date"
    | "checkbox"
    | "switch"
    | "fixed"
    | "symbol"
    | "form"
    | "button"
    | null = booleanModes.includes(currentVariant)
    ? currentVariant === "switch"
      ? "switch"
      : "checkbox" // TOOD: if more boolean modes update this
    : singleDateModes.includes(currentVariant)
    ? ("date" as const)
    : symbolModes.includes(currentVariant)
    ? ("symbol" as const)
    : refreshModes.includes(currentVariant)
    ? "button"
    : config.inputOptions.every(
        (option: FormInputRowMode) =>
          fixedModes.includes(option.optionVariant) || formModes.includes(option.optionVariant)
      )
    ? null
    : fixedModes.includes(currentVariant)
    ? "fixed"
    : formModes.includes(currentVariant)
    ? "form"
    : ("text" as const);

  var currentState: unknown;
  if (numberModes.includes(currentVariant) && props.currentState.currentModeStates[currentModeIndex] === 0) {
    currentState = 0;
  } else if (
    numberModes.includes(currentVariant) &&
    typeof props.currentState.currentModeStates[currentModeIndex] === "string"
  ) {
    currentState = onNumberModeValueChange(currentVariant)(
      props.currentState.currentModeStates[currentModeIndex] as string
    );
  } else {
    currentState = props.currentState.currentModeStates[currentModeIndex] || "";
  }

  const currentError =
    props.currentState.currentModeErrors === undefined
      ? undefined
      : props.currentState.currentModeErrors[currentModeIndex];

  const currentStartAdornment =
    currentInputOption.startAdornment || (dollarModes.includes(currentVariant) ? "$" : undefined);
  const currentEndAdornment = currentInputOption.endAdornment || (currentVariant === "percent" ? "%" : undefined);

  const currentCaption =
    currentInputOption.caption === undefined || currentInputOption.caption === null
      ? config.defaultCaption
      : currentInputOption.caption;

  // This function handles toggling between modes here to create more intuitive conversions across numbers
  // by converting the number to the base unit and propogating it across any other mode that also has a base unit conversion factor
  // e.g. 12 months --> 1 years
  // TODO: should this be the place we set up new state for the nested forms?
  function handleModeToggle(
    event: React.MouseEvent<HTMLElement> | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    newModeIndex: number | null
  ) {
    // TODO: add string / select mode bridge here for more intuitive mode conversion?
    if (newModeIndex !== null && newModeIndex !== currentModeIndex) {
      const oldModeVariant = currentVariant;
      // const newModeVariant = config.inputOptions[newModeIndex].optionVariant;
      if (
        currentState !== null &&
        typeof currentState === "number" &&
        numberModes.includes(oldModeVariant) &&
        (currentInputOption.baseUnitConversionFactor !== undefined || oldModeVariant === "percent")
      ) {
        const baseUnitValue =
          currentState *
          (currentInputOption.baseUnitConversionFactor === undefined
            ? oldModeVariant === "percent"
              ? 0.01
              : 1 // This case shouldn't be reached
            : currentInputOption.baseUnitConversionFactor); // TODO: avoid divide by 0?

        config.inputOptions.forEach((option, index) => {
          if (index === currentModeIndex) {
            return;
          }
          if (!numberModes.includes(option.optionVariant)) {
            return;
          }
          if (option.baseUnitConversionFactor === undefined) {
            if (option.optionVariant !== "percent") {
              return;
            } else {
              updateOtherModeState(baseUnitValue * 100, index);
              return;
            }
          }
          const otherModeValue = baseUnitValue / option.baseUnitConversionFactor;
          updateOtherModeState(
            onNumberModeValueChange(option.optionVariant)(otherModeValue as unknown as string),
            index
          );
        });
      }
      updateCurrentModeIndex(newModeIndex);
    }
  }

  const sharedInputFieldProps = {
    variant: "outlined" as const,
    fullWidth: true,
    onKeyDown: props.catchEnterKeyDownToSubmit,
    // type: numberModes.includes(currentVariant) ? "number" : undefined,
    select: selectModes.includes(currentVariant) ? true : undefined,
    label: currentFieldLabel,
    disabled: fixedModes.includes(currentVariant) || disabled === true,
    error: currentError !== null && currentError !== undefined,
    helperText: currentError !== null && currentError !== undefined ? <>{currentError}</> : <>{currentFieldHint}</>,
    InputProps: Object.assign(
      currentStartAdornment
        ? {
            startAdornment: <InputAdornment position="start">{currentStartAdornment}</InputAdornment>,
          }
        : {},
      currentEndAdornment
        ? {
            endAdornment: <InputAdornment position="end">{currentEndAdornment}</InputAdornment>,
          }
        : {},
      numberModes.includes(currentVariant) ? { inputComponent: NumericFormatComponent as any } : {}
    ),
    onBlur: () => {
      if (
        props.config.invalidIfEdited &&
        props.currentState.originalRowState !== undefined &&
        props.config.invalidIfEditedErrorText !== undefined &&
        props.config.invalidIfEditedErrorText !== null &&
        props.config.invalidIfEditedErrorText !== ""
      ) {
        props.updateFormRowErrorAtIndex(props.config.invalidIfEditedErrorText);
      }
      if (
        numberModes.includes(currentVariant) &&
        currentState !== undefined &&
        currentState !== null &&
        currentState !== ""
      ) {
        var currNumberMin = undefined;
        var overrideInclusiveMin = false;
        if (currentVariant === "price") {
          currNumberMin = 0;
          overrideInclusiveMin = true;
        }
        if (currentInputOption.numberMin !== undefined && currentInputOption.numberMin !== null) {
          currNumberMin =
            currNumberMin === undefined
              ? currentInputOption.numberMin
              : Math.max(currNumberMin, currentInputOption.numberMin);
        }

        if (currNumberMin !== undefined) {
          if (
            (currentInputOption.numberMinMaxEndpointExclusive ||
              (currNumberMin === 0 && overrideInclusiveMin === true)) &&
            (currentState as number) <= currNumberMin
          ) {
            props.updateFormRowErrorAtIndex("must be more than " + currNumberMin.toLocaleString());
          } else if ((currentState as number) < currNumberMin) {
            props.updateFormRowErrorAtIndex("must be at least " + currNumberMin.toLocaleString());
          }
        }
        if (currentInputOption.numberMax !== undefined && currentInputOption.numberMax !== null) {
          if (
            currentInputOption.numberMinMaxEndpointExclusive &&
            (currentState as number) >= currentInputOption.numberMax
          ) {
            props.updateFormRowErrorAtIndex("must be less than " + currentInputOption.numberMax.toLocaleString());
          } else if ((currentState as number) > currentInputOption.numberMax) {
            props.updateFormRowErrorAtIndex("must be at most " + currentInputOption.numberMax.toLocaleString());
          }
        }
      }
      if (
        singleDateModes.includes(currentVariant) &&
        currentState !== undefined &&
        currentState !== null &&
        currentState !== ""
      ) {
        if (currentInputOption.dateMin !== undefined && currentInputOption.dateMin !== null) {
          const currDateMin = dayjs(currentInputOption.dateMin);
          if (currentInputOption.dateMinMaxEndpointExclusive && (currentState as Dayjs) <= currDateMin) {
            props.updateFormRowErrorAtIndex("must be after " + currDateMin.format("MM/DD/YYYY"));
          } else if ((currentState as Dayjs) < currDateMin) {
            props.updateFormRowErrorAtIndex("must be on or after " + currDateMin.format("MM/DD/YYYY"));
          }
        }
        if (currentInputOption.dateMax !== undefined && currentInputOption.dateMax !== null) {
          const currDateMax = dayjs(currentInputOption.dateMax);
          if (currentInputOption.dateMinMaxEndpointExclusive && (currentState as Dayjs) >= currDateMax) {
            props.updateFormRowErrorAtIndex("must be before " + currDateMax.format("MM/DD/YYYY"));
          } else if ((currentState as Dayjs) > currDateMax) {
            props.updateFormRowErrorAtIndex("must be on or before " + currDateMax.format("MM/DD/YYYY"));
          }
        }
      }
    },
  };

  //TODO: add parse versions for different mode options
  const textFieldOnChange = (newValue: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) =>
    numberModes.includes(currentVariant)
      ? updateCurrentModeState(
          isNaN(onNumberModeValueChange(currentVariant)(newValue.target.value))
            ? null
            : onNumberModeValueChange(currentVariant)(newValue.target.value)
        ) // Parses number modes using special logic
      : updateCurrentModeState(newValue.target.value);

  const textFieldChildrenPayloadForSelect =
    selectModes.includes(currentVariant) && currentInputOption.selectOptions !== undefined
      ? currentInputOption.selectOptions.map((option) => (
          <MenuItem key={option} value={option}>
            {option}
          </MenuItem>
        ))
      : null;

  const dateFieldOnChange = (newValue: Dayjs | null) => {
    updateCurrentModeState(newValue);
  };

  function booleanModeFormGroupFromControlComponent(control_component: React.ReactElement) {
    // <FormControl component="fieldset" variant="standard">
    {
      /* <FormLabel component="legend">Assign responsibility</FormLabel> */
    }
    return (
      <FormControl component="fieldset" fullWidth>
        <FormControlLabel
          label={currentFieldLabel}
          disabled={fixedModes.includes(currentVariant) || disabled === true}
          checked={currentState ? true : false}
          onChange={(e: SyntheticEvent<Element, Event>, checked: boolean) => updateCurrentModeState(checked)}
          control={control_component}
        />
        {currentFieldHint ? <FormHelperText>{currentFieldHint}</FormHelperText> : null}
      </FormControl>
    );
  }

  const potentialInputFields = {
    fixed: <TextField {...sharedInputFieldProps} variant="filled" value={undefined} disabled={true} />,
    form: <TextField {...sharedInputFieldProps} variant="filled" value={undefined} disabled={true} />,
    symbol: (
      <SymbolAutocomplete
        TextFieldProps={sharedInputFieldProps}
        symbol={currentState as string}
        setSymbol={(newSymbol: string | null) => updateCurrentModeState(newSymbol)}
      />
    ),
    text: (
      <TextField {...sharedInputFieldProps} value={currentState} onChange={textFieldOnChange}>
        {textFieldChildrenPayloadForSelect}
      </TextField>
    ),
    date: (
      <DateField
        {...sharedInputFieldProps}
        value={currentState === null ? null : dayjs(currentState?.toString())}
        clearable
        onChange={dateFieldOnChange}
      />
    ),
    checkbox: booleanModeFormGroupFromControlComponent(<Checkbox />),
    switch: booleanModeFormGroupFromControlComponent(<Switch />),
    button: (
      <Box sx={{ display: "flex", justifyContent: "center", flexGrow: 1 }}>
        <LoadingButton
          size="large"
          color="info"
          variant="contained"
          loading={props.refreshing}
          disabled={disabled === true}
          onClick={() => props.refresh(currentInputOption.refreshEndpoint)}
          sx={{ alignSelf: "center" }}
        >
          <>
            {getModeIconFromOption(currentInputOption) === null ? null : (
              <Box sx={{ pr: 1 }}>
                <>{getModeIconFromOption(currentInputOption)}</>
              </Box>
            )}
            {currentInputOption.modeText}
          </>
        </LoadingButton>
      </Box>
    ),
  };

  const sectionTitle = config.sectionTitle === undefined ? null : config.sectionTitle;

  return (
    <Box>
      {config.sectionTitleToolTip ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "flex-start",
            alignItems: "center",
            flexGrow: 0,
          }}
        >
          <Tooltip title={config.sectionTitleToolTip} placement="top-end" arrow enterDelay={enterToolTipDelay}>
            <Box sx={{ pb: 1 }}>
              {sectionTitle}
              <IconButton size="small">
                <InfoOutlinedIcon color="info" fontSize="inherit" />
              </IconButton>
            </Box>
          </Tooltip>
        </Box>
      ) : (
        <Box sx={{ pb: sectionTitle ? 1 : 0 }}>{sectionTitle}</Box>
      )}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: isMobile ? "column-reverse" : "row",
          gap: isMobile ? 2 : 3,
          alignItems: "flex-start",
          maxWidth: isMobile ? "75vw" : undefined,
        }}
      >
        {currentInputFieldToDisplay === null ? null : potentialInputFields[currentInputFieldToDisplay]}
        {config.inputOptions.length > 1 &&
          (config.selectModeChangeInsteadOfToggle ? (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                justifyContent: currentInputFieldToDisplay === null ? "flex-start" : "flex-end",
                alignItems: "center",
              }}
            >
              <TextField
                select
                label={config.selectModeChangeInsteadOfToggleLabel || "Mode Select"}
                variant={currentInputFieldToDisplay === null ? "outlined" : "filled"}
                value={currentModeIndex}
                onChange={(e) => handleModeToggle(e, parseInt(e.target.value))}
                // autoWidth
                fullWidth
                disabled={disabled === true || nestedRefreshing}
                sx={{ alignSelf: "center", minWidth: "100px" }}
              >
                {config.inputOptions.map((option, index) => makeMenuItemFromOptionIndex(option, index))}
              </TextField>
            </Box>
          ) : (
            <ToggleButtonGroup
              value={currentModeIndex}
              onChange={handleModeToggle}
              exclusive
              size="large"
              sx={{
                flex: 1,
                whiteSpace: "nowrap",
                justifyContent: currentInputFieldToDisplay === null ? "flex-start" : "flex-end",
              }}
              disabled={disabled === true || nestedRefreshing}
            >
              {config.inputOptions.map((option, index) => makeToggleButtonFromOptionIndex(option, index))}
            </ToggleButtonGroup>
          ))}
      </Box>
      <Box sx={{ position: "relative" }}>
        {props.config.inputOptions.map((rowConfig, index) =>
          formModes.includes(rowConfig.optionVariant) && rowConfig.nestedFormConfig ? (
            <Collapse key={index} in={currentModeIndex === index && rowConfig.nestedFormConfig !== undefined}>
              <Paper variant="outlined" sx={{ padding: 2, background: "transparent" }}>
                <ConfiguredForm
                  config={rowConfig.nestedFormConfig}
                  setConfig={(
                    newConfig:
                      | (FormConfig | undefined)
                      | ((oldConfig: FormConfig | undefined) => FormConfig | undefined)
                  ) => props.setNestedConfigAtIndex(newConfig, index)}
                  refreshing={nestedRefreshing}
                  setRefreshing={setNestedRefreshing}
                  state={props.currentState.currentModeStates[index] as FormRowState[]}
                  setState={(newState: FormRowState[] | ((oldState: FormRowState[]) => FormRowState[])) =>
                    props.updateFormRowStateAtIndex(newState, index)
                  }
                  catchEnterKeyDownToSubmit={props.catchEnterKeyDownToSubmit}
                  disabled={disabled || currentModeIndex !== index}
                />
              </Paper>
            </Collapse>
          ) : null
        )}
      </Box>
      {currentCaption ? (
        <Typography variant="caption" color="text.secondary">
          {currentCaption}
        </Typography>
      ) : null}
    </Box>
  );
}

export default FormInputRow;
